import type { AlarmSummary, IssueFilter, NetworkStatus, OverviewCategory } from '../../lib/overviewStatus'
import { MONITORING_DELAYED } from '../../lib/freshness'
import { stationsCardNote } from '../../lib/overviewStatus'

export const ISSUE_CATEGORIES: { id: OverviewCategory; title: string; detail: string }[] = [
  { id: 'alarms', title: 'Alarms and Warnings', detail: 'Engineering thresholds' },
  { id: 'missing', title: 'Late or missing readings', detail: 'Overdue stations and sensor channels' },
  { id: 'collection', title: 'Data collection', detail: 'Source connection or data processing failed' },
  { id: 'checks', title: 'Monitoring delayed', detail: 'Automatic checks are unavailable' },
]

/** Three everyday indicators; service faults appear only when actionable. */
export function StatusCards({ network, alarm, alarmTone, counts, known, stationsFailed, loading, filter, onFilter }: {
  /** Null while the station list loads or after it failed to load. */
  network: NetworkStatus | null
  alarm: AlarmSummary
  /** The alarm card's colour, beside its words: only for current alarms. */
  alarmTone: 'alarm' | 'warning' | 'none'
  /** New issues; a known one leaves all but the alarms' count (categoryCounts). */
  counts: Record<OverviewCategory, number>
  /** How many are known, said beside the count. */
  known: Record<OverviewCategory, number>
  stationsFailed: boolean
  loading: boolean
  filter: IssueFilter
  onFilter: (filter: IssueFilter) => void
}) {
  return <><div className="ov-status-cards" role="group" aria-label="Network summary">
    <div className="ov-status-card ov-card-network">
      <span>Stations</span>
      {network
        ? <strong>{network.receiving}<em>of {network.total} receiving</em></strong>
        : <strong>{loading ? '…' : 'N/A'}</strong>}
      <small>{network ? stationsCardNote(network) : loading ? 'Loading stations' : 'Station status unavailable'}</small>
    </div>
    {ISSUE_CATEGORIES.filter(category => category.id === 'alarms' || category.id === 'missing').map(category => {
      const selected = filter === category.id
      const isAlarms = category.id === 'alarms'
      // Station issues need the station list; without it the count would
      // read as "nothing wrong" rather than "not known".
      const unavailable = !isAlarms && stationsFailed
      const value = isAlarms ? alarm.value === MONITORING_DELAYED ? 'Unavailable' : alarm.value : loading ? '…' : unavailable ? 'N/A' : String(counts[category.id])
      const note = isAlarms ? alarm.note : unavailable ? 'Awaiting current evaluation'
        : known[category.id] ? `${category.detail} · ${known[category.id]} known` : category.detail
      return <button key={category.id} type="button" aria-pressed={selected}
        className={`ov-status-card ov-card-${category.id}${selected ? ' selected' : ''}`}
        onClick={() => onFilter(selected ? 'all' : category.id)}>
        <span>{category.title}</span>
        <strong className={[isAlarms && alarm.word ? 'ov-value-word' : '', isAlarms && alarmTone !== 'none' ? `ov-tone-${alarmTone}` : '']
          .filter(Boolean).join(' ') || undefined}>{value}</strong>
        <small className={isAlarms && alarmTone !== 'none' ? `ov-tone-${alarmTone}` : undefined}>{note}</small>
      </button>
    })}
  </div>
    {(counts.collection > 0 || filter === 'collection') && <div className="ov-service-notice" role="status"><div><strong>Data collection · {counts.collection} {counts.collection === 1 ? 'issue' : 'issues'}{known.collection ? `, ${known.collection} known` : ''}</strong><p>A source connection or data processing has failed. Open the affected station for details.</p></div><button type="button" className="button" onClick={() => onFilter(filter === 'collection' ? 'all' : 'collection')}>{filter === 'collection' ? 'Show all issues' : 'View collection issues'}</button></div>}
    {(counts.checks > 0 || filter === 'checks') && <div className="ov-service-notice" role="status"><div><strong>Monitoring delayed · {counts.checks} checks</strong><p>Automatic status checks are unavailable. Refresh; if this continues, contact your administrator. No manual check-off is needed.</p></div><button type="button" className="button" onClick={() => onFilter(filter === 'checks' ? 'all' : 'checks')}>{filter === 'checks' ? 'Show all issues' : 'View affected checks'}</button></div>}
  </>
}
